import { Component, ViewChild, computed, input, output, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import {
  ComponentSearchItem,
  applyComponentFilter,
  componentHasMatchedProperty,
  findMatchingProperty,
  matchesComponentName
} from './toolbar-search.helper';

export interface ComponentSelectedEvent {
  key: string;
  matchedProperty: string | null;
}

@Component({
  selector: 'app-toolbar-actions',
  templateUrl: './toolbar-actions.component.html',
  standalone: false
})
export class ToolbarActionsComponent {
  readonly compactMode = input<boolean>(true);
  readonly panelMode = input<boolean>(false);
  readonly wizardMode = input<boolean>(false);
  readonly wizardStepIndex = input<number>(0);
  readonly wizardTotalSteps = input<number>(0);
  readonly wizardStepLabel = input<string>('');
  readonly wizardStepLabels = input<readonly string[]>([]);
  readonly panelColumnItems = input<MenuItem[]>([]);
  readonly allComponents = input<ComponentSearchItem[]>([]);

  readonly panelToggle = output<void>();
  readonly wizardToggle = output<void>();
  readonly wizardPrevStep = output<void>();
  readonly wizardNextStep = output<void>();
  readonly wizardStepSelected = output<number>();
  readonly compactModeToggle = output<void>();
  readonly unitsConverterOpen = output<void>();
  readonly componentSelected = output<ComponentSelectedEvent>();
  readonly schematicToggled = output<void>();

  private readonly _searchTerm = signal('');
  private _matchedProperty: string | null = null;

  public readonly filteredComponents = computed(() =>
    applyComponentFilter(this.allComponents(), this._searchTerm())
  );

  public readonly canGoPrevWizardStep = computed(() => this.wizardMode() && this.wizardStepIndex() > 0);
  public readonly canGoNextWizardStep = computed(() => this.wizardMode() && this.wizardStepIndex() < this.wizardTotalSteps() - 1);

  public selectedItems: any;

  @ViewChild('search') private _searchElement: any;
  @ViewChild('stepListPopover') private _stepListPopover: any;

  public onStepLabelClick(event: MouseEvent): void {
    this._stepListPopover?.toggle(event);
  }

  public selectStep(index: number, event?: MouseEvent): void {
    this.wizardStepSelected.emit(index);
    this._stepListPopover?.hide(event);
  }

  /** Called by the parent container when the keyboard shortcut opens the search panel. */
  showAndFocusSearch(): void {
    if (this._searchElement) {
      this._searchElement.show();
      this._focusSearch();
    }
  }

  populate(): void {
    this._searchTerm.set('');
    this._focusSearch();
  }

  handleFilter(event: any): void {
    this._searchTerm.set(event.filter?.toLowerCase() || '');
  }

  filteredProperty(component: ComponentSearchItem): string | null {
    const result = findMatchingProperty(component, this._searchTerm());
    if (result) this._matchedProperty = result;
    return result;
  }

  filteredName(component: ComponentSearchItem): string | null {
    return matchesComponentName(component, this._searchTerm());
  }

  componentContainsProperty(component: ComponentSearchItem): string | undefined {
    return componentHasMatchedProperty(component, this._matchedProperty);
  }

  onComponentAdded(event: any): void {
    const selected = event?.value?.[0];
    if (!selected?.key) return;
    this.componentSelected.emit({ key: selected.key, matchedProperty: this._matchedProperty });
    this.selectedItems = [];
  }

  private _focusSearch(): void {
    setTimeout(() => {
      const searchInput = this._searchElement?.el?.nativeElement?.querySelector('.p-multiselect-filter');
      if (searchInput && document.contains(searchInput)) {
        searchInput.focus();
      }
    }, 150);
  }
}
